import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, MapperAPI, PanelAPI, TextAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { ClickEvent, FetchEvent, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { isFunction } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';
import { FragmentNames, NeonatalTpnStartAliases } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ActionType, AdminType, InfusionHourStatus, InfusionItemStatus } from '../../../../../constants/infusion-solutions';
import { ApplicationRoute } from '../../../../../constants/routes';
import { BarcodeUtils } from '../../../../../utils/barcode-utils';
import { YesNoType } from '../../../../../utils/enum-utils';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../../utils/parameters';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import { AdminModalsService } from '../../../services/admin-modals-service';
import MedicationService from '../../../services/medication-service';
import { AdminByScheduleParams } from '../../administration-schedule/administration-schedule-params';
import { UnitMeasures, UnitMeasuresDose } from '../../enteral-nutrition/enteral-nutrition-start/enteral-nutrition-start-type';
import { ResponseBodyProps } from '../../enteral-nutrition/enteral-nutrition-type';
import { NeonatalTpnDevice, NeonatalTpnType } from '../neonatal-tpn-types';
import { PrescriptionType } from '../../../services/timeline-service';
import NeonatalTpnUtils from '../neonatal-tpn-utils';
import BarcodeService from '../../../../../services/barcode-service';

@Controller({ component: NeonatalTpnStartAliases.NEONATAL_TPN_START_LAYOUT, fragment: FragmentNames.NEONATAL_TPN_START })
export default class NeonatalTpnStartController extends BaseController {

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_NAME_TEXT)
  private nameTextApi!: TextAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_DATE_TIME_TEXT)
  private dateTimeTextApi!: TextAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_NEW_DATE_INPUT)
  private newDateInputApi!: InputAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_FORM)
  private formApi!: FormAPI

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_DEVICE_MAPPER)
  private deviceMapperApi!: MapperAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_UNITY_FIELD)
  private unityFieldApi!: FieldAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_INFUSION_FORM_FIELD)
  private infusionFormFieldApi!: FieldAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_DEVICE_FIELD)
  private deviceFieldApi!: FieldAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_BARCODE_PANEL)
  private barcodePanelApi!: PanelAPI

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_BUTTON)
  private startButtonApi!: ButtonAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_CARD_DECK)
  private cardDeckApi!: DeckAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_CARDS_ACCORDION_PANEL)
  private cardsAccordionPanelApi!: PanelAPI;

  @Inject(NeonatalTpnStartAliases.NEONATAL_TPN_START_BARCODE)
  private barcodeApi!: BarcodeAPI;

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_FORM)
  handleMountForm(): void {
    InformationBarUtils.collapsedBar = true;
    const { neonatalTpn } = this.context.getBulkData() as { neonatalTpn: NeonatalTpnType };

    AdminByScheduleParams.readParam = { noCardsRead: true };
    const displayDevices = FoundationUtils
      .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);

    this.context.set({
      hasDevice: displayDevices === YesNoType.NO,
      materialCode: neonatalTpn.materialSequence,
    });
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_NEW_DATE_INPUT)
  handleMountNewDateInput(): void {
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }
    const newDate = moment().utc().format(ISO_FULL_DATE_FORMAT);

    this.dateTimeTextApi.setValue(moment(neonatalTpn.scheduleDate).utc().format(ISO_FULL_DATE_FORMAT));
    this.newDateInputApi.setValue(newDate);
    this.context.set({ currentTime: newDate, dateTime: newDate });
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_NAME_FIELD)
  handleMountMaterialNameField({ target }: EventData<FieldAPI>): void {
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }
    target.setValue(neonatalTpn.materialDescription);
    this.nameTextApi.setValue(neonatalTpn.materialDescription)
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_INFUSION_FORM_AUTOCOMPLETE)
  handleMountInfusionFormAutocomplete(): void {
    this.fetchInfusionForm().then((response) => {
      const currentRecord = this.formApi.getRecord();

      this.formApi.setRecord({
        ...currentRecord,
        infusionType: {
          unitMeasure: response[0].unitMeasure,
          unitMeasureValue: response[0].unitMeasureValue
        }
      })
    }).catch((error) => {
      const errorMessage = error.body.errors[0];
      MedicationService.showToast(errorMessage, ToastLevel.ERROR);
    });
  }

  private fetchInfusionForm(): Promise<ResponseBodyProps[]> {
    const infusionFormDomainSequence = 1883;
    const activeSituation = 'A';

    return HttpService.get(Endpoint.EMAR_FIND_INFUSIONS_FORM, { domain: infusionFormDomainSequence, situation: activeSituation })
      .then(res => res.body as ResponseBodyProps[])
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_DEVICE_AUTOCOMPLETE)
  handleMountDeviceAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    if (this.shouldDisplayDevices()) {
      target.setEntry('description');
      target.setPrimaryKey('description');
    }
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_DEVICE_MAPPER)
  handleMountDeviceMapper(): void {
    if (this.shouldDisplayDevices()) {
      this.deviceMapperApi.setMap({ value: 'description' });
    }
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_DEVICE_FETCH)
  handleMountDeviceFetch({ target }: EventData<FetchAPI>): void {
    if (this.shouldDisplayDevices()) {
      const { encounterId } = this.historyService.getCurrentRouteParams();
      const deviceStatus = FoundationUtils
        .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
      this.context.set({ deviceStatus, encounterId });
      target.setEndpoint(Endpoint.EMAR_FIND_ADULT_TPN_DEVICE);
    }
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_VOLUME_STEP_FIELD)
  handleMountVolumeStepField({ target }: EventData<FieldAPI>): void {
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }
    const { totalVolume } = neonatalTpn
    target.setValue(totalVolume ?? '')
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_INFUSION_SPEED_FIELD)
  handleMountInfusionSpeed({ target }: EventData<FieldAPI>): void {
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }
    target.setValue(neonatalTpn.dripQuantity ?? 0)
  }

  @OnAfterFetch(NeonatalTpnStartAliases.NEONATAL_TPN_START_UNITY_FETCH)
  handleAfterFetchUnityFetch(event: FetchEvent): void {
    const unityMeasure = event.data as UnitMeasuresDose[]
    this.unityFieldApi.setVisible(!!unityMeasure.length)
  }

  @OnAfterFetch(NeonatalTpnStartAliases.NEONATAL_TPN_START_INFUSION_FORM_FETCH)
  handleAfterFetchInfusionFormFetch(event: FetchEvent): void {
    const infusionForm = event.data as UnitMeasures[]
    this.infusionFormFieldApi.setVisible(!!infusionForm.length)
  }

  @OnAfterFetch(NeonatalTpnStartAliases.NEONATAL_TPN_START_DEVICE_FETCH)
  handleAfterFetchDeviceFetch(event: FetchEvent): void {
    const deviceFetchApi = event.target as unknown as FetchAPI;
    const devices = event.data as NeonatalTpnDevice[]

    deviceFetchApi.setParameters({ loading: false }, { skip: true });
    deviceFetchApi.clearPersistedState();

    this.deviceFieldApi.setVisible(!!devices.length)
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_BUTTON)
  handleMountStartButton(): void {
    this.setStartButtonValue();
    this.validateIfBarcodeAndAccordionCardsIsVisible();
  }

  @OnMount([NeonatalTpnStartAliases.NEONATAL_TPN_START_INFUSION_FORM_FIELD, NeonatalTpnStartAliases.NEONATAL_TPN_START_DEVICE_FIELD])
  handleMountDeviceField({ target }: EventData<FieldAPI>): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    target.setVisible(itemStatus === InfusionItemStatus.NOT_STARTED)
  }

  @OnClick(NeonatalTpnStartAliases.NEONATAL_TPN_START_BUTTON)
  async handleClickStartButton(): Promise<void> {
    const isValid = await this.formApi.validate();
    if (isValid) {
      this.submitNeonatalTpn()
    }
  }

  @OnClick(NeonatalTpnStartAliases.NEONATAL_TPN_START_CARD)
  handleClickCard(event: ClickEvent): void {
    const scheduleSequence = event.value.scheduleSequence;

    BarcodeUtils.dialogModalConfirm(scheduleSequence, this.resetCard);
  }

  @OnMount(NeonatalTpnStartAliases.NEONATAL_TPN_START_BARCODE)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess(NeonatalTpnStartAliases.NEONATAL_TPN_START_BARCODE)
  handleReadSuccessBarcode(event: ReadEvent<BarcodeData>): void {
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }

    const { preventDefault } = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }
    const barcodeRead = event.item.value.trim();

    HttpService.get(Endpoint.EMAR_ADULT_TPN_CHECK_LABEL, {
      labelId: barcodeRead,
      prescriptionId: neonatalTpn.prescriptionNumber,
      scheduleDate: neonatalTpn.scheduleDate
    }).then((response) => {
      const isReading = response.body

      if (!isReading) {
        this.resetCard();
        MedicationService.showToast(MessageService.translate('text.label-is-incorrect'), ToastLevel.ERROR);
        return
      }

      this.setItemInCard(true)
      MedicationService.showToast(MessageService.translate('text.medication-read-with-success'), ToastLevel.SUCCESS);
      this.startButtonApi.setEnabled(true);
      this.barcodeApi.setEnabled(false);
      this.barcodeApi.close();
      this.context.set({ allItemsRead: true });

    }).catch(error => {
      if (error.body && error.body.errors) {
        error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
      }
    })
  }

  private validateIfBarcodeAndAccordionCardsIsVisible(): void {
    const checkMedicationBeforeAdmin = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const isRestartSolution = this.context.get('actionType') === ActionType.RESTART_SOLUTION;

    if (checkMedicationBeforeAdmin === YesNoType.NO || isRestartSolution) {
      this.barcodePanelApi.setVisible(false);
      this.cardsAccordionPanelApi.setVisible(false);
      this.startButtonApi.setEnabled(true);
      return
    }
    this.resetCard();
  }

  resetCard = (): void => {
    this.setItemInCard(false)
    this.barcodeApi.setEnabled(true)
    this.startButtonApi.setEnabled(false);
  }

  private setItemInCard(isRead: boolean): void {
    const bagExpression = MessageService.translate('text.bag');
    const neonatalTpnExpression = this.getExpressionNptByItemType();
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }

    this.cardDeckApi.setData([{
      barcode: '',
      doseQuantity: 1,
      materialDescription: `${bagExpression} ${neonatalTpnExpression}`,
      read: isRead,
      scheduleSequence: neonatalTpn.scheduleSequence,
      unitMeasureCode: 'Bs',
      unitMeasureDoseCode: 'Bs',
    }])
  }

  private getExpressionNptByItemType(): string {
    const { itemType } = this.historyService.getCurrentRouteParams();

    if (itemType === PrescriptionType.NEONATAL_PARENTERAL_NUTRITION) {
      return MessageService.translate('text.neonatal-npt');
    }

    return MessageService.translate('txt.pediatric-npt');

  }

  private submitNeonatalTpn(): void {
    const requestBody = this.getNeonatalTpnRequestBody();

    this.startButtonApi.setEnabled(false);

    HttpService.post(Endpoint.EMAR_START_NEONATAL_TPN, {}, { body: requestBody })
      .then((response) => {
        this.showResponseMessage(response.body as string)
        this.redirectToTimeline()
      })
      .catch((error) => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
        }
      })
      .finally(() => {
        this.startButtonApi.setEnabled(true);
      })
  }

  private getNeonatalTpnRequestBody(): Record<string, Value> {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }
    const formRecord = this.formApi.getRecord();
    const currentTime = this.newDateInputApi.getValue()
    const formatedInfusionSpeed = String(formRecord.infusionSpeed).replace(',', '.');
    const actionType = this.context.get('actionType')
    const infusionTypeForm = formRecord.infusionType as { unitMeasure?: string, unitMeasureValue?: string, };
    const infusionType = this.getInfusionType(infusionTypeForm.unitMeasureValue);
    const device = formRecord.device as NeonatalTpnDevice;

    const body = {
      actionType,
      changeDate: currentTime,
      dateTime: neonatalTpn.scheduleDate,
      deviceSequence: Number(device?.domain),
      encounterNumber: encounterId,
      infusionSpeedQuantity: Number(formatedInfusionSpeed),
      infusionType,
      justifyDescription: '',
      observationDescription: formRecord.observation ?? '',
      prescriptionNumber: Number(neonatalTpn.prescriptionNumber),
      solutionSequence: neonatalTpn.materialSequence,
      solutionType: NeonatalTpnUtils.getSolutionTypeByItemType(neonatalTpn.itemType || ''),
      volumePhaseQuantity: Number(String(formRecord.volumePhase).split(' ')[0])
    } as Record<string, Value>;

    return body;
  }

  private getInfusionType(unitMeasureValue?: string): AdminType {
    return unitMeasureValue !== AdminType.OPEN && unitMeasureValue !== AdminType.TIME &&
      unitMeasureValue !== AdminType.INFUSION_SPEED ? AdminType.INFUSION_SPEED : unitMeasureValue;
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    const actionType = this.context.get('actionType')
    switch (actionType) {
      case ActionType.START_SOLUTION:
        MedicationService.showToast(MessageService.translate('text.infusion-started-with-success'), ToastLevel.SUCCESS);
        break;
      case ActionType.RESTART_SOLUTION:
        MedicationService.showToast(MessageService.translate('text.infusion-restarted-with-success'), ToastLevel.SUCCESS);
        break;
      default:
        break;
    }
  }

  private setStartButtonValue(): void {
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }
    this.startButtonApi.setValue(MessageService.translate('button.infusion-viewer.start'));
    const solutionItemStatusInterrupted = neonatalTpn.itemStatus === InfusionItemStatus.II || neonatalTpn.itemStatus === InfusionItemStatus.INTERRUPTED;

    if (neonatalTpn.status === InfusionHourStatus.INTERRUPTED && solutionItemStatusInterrupted) {
      this.context.set({ actionType: ActionType.RESTART_SOLUTION })
      this.startButtonApi.setValue(MessageService.translate('button.infusion-viewer.restart-solution'));
      return;
    }

    if (neonatalTpn.status !== InfusionHourStatus.STARTED) {
      this.context.set({ actionType: ActionType.START_SOLUTION })
      this.startButtonApi.setValue(MessageService.translate('button.infusion-viewer.start'));
      return;
    }

    this.startButtonApi.setVisible(false)
  }

  private shouldDisplayDevices(): boolean {
    const displayDevices = FoundationUtils
      .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
    return displayDevices === YesNoType.YES;
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.NEONATAL_TPN_START);
    AdminModalsService.redirectToTimeline(FragmentNames.NEONATAL_TPN_VIEWER);
  }

}